import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'

//evertel
import { Button, InfoBox, Col, Row, Modal, ModalBody, ModalHeader, ModalFooter, SuccessCheckmark, FormInput, useUI } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { Analytics } from '@evertel/analytics'
import { ConsumeInviteController } from '@evertel/invites'
import { SessionService } from '@evertel/session/SessionService'
import { DepartmentController } from '@evertel/department'
import { CurrentUserDepartmentsController } from '@evertel/blue-user'

interface JoinDepartmentModalProps {
    isOpen: boolean
    onClose: () => void
}

const JoinDepartmentModal:React.FC<JoinDepartmentModalProps> = ({
    isOpen,
    onClose
}) => {

    const analytics = useService(Analytics)
    const sessionService = useService(SessionService, [])

    const consumeInviteController = useService(ConsumeInviteController, [])
    const departmentController = useService(DepartmentController, [])
    const currentUserDepartmentsController = useService(CurrentUserDepartmentsController, [])

    const navigate = useNavigate()

    const { addToast } = useUI()

    const [error, setError] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [token, setToken] = useState('')
    const [tokenValid, setTokenValid] = useState(false)

    useEffect(() => {
        if (isOpen) {
            // log modal view on open
            analytics.logView('/account/profile/join-dept')
        }
    }, [isOpen])

    const consumeInviteToken = async () => {
        // no access code, can't continue
        if (!token) {
            setError('You must enter a valid agency access code to continue. Or you can simply click on the invite link in your invite email or text')
            return
        }

        setIsLoading(true)
        setError(null)

        try {
            const response = await consumeInviteController.consumeInvite(token)

            if (response?.action === 'DEPARTMENT_INVITE') {
                // Set the new department as default
                const departmentId = response.departmentsAccess.departmentId
                if (departmentId) {
                    sessionService.setSelectedDeparmentId(departmentId)

                    await departmentController.init(departmentId)
                }

                addToast({
                    message: `Successfully added to ${departmentController.department?.name || 'the agency'}`,
                    color: 'success'
                })

                navigate('/landing')

            }

            setTokenValid(true)

        } catch (error: any) {
            // GCWSCSR-915: the API now rejects seat-overage consumes up front
            // (nothing is partially applied), so always surface the error
            setError(error.message || error)
            setTokenValid(false)
        } finally {
            setIsLoading(false)
        }
    }

    const onTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setToken(e.target.value)
    }

    const onCloseWindow = () => {
        setError(null)
        setIsLoading(false)
        setToken('')
        setTokenValid(false)
        onClose()
    }


    const hasDepartments = currentUserDepartmentsController.departments.length > 0

    return (
        <Modal
            visible={isOpen}
            onClose={onCloseWindow}
            className="join-dept-modal"
        >
            <ModalHeader
                title={hasDepartments ? 'Join additional agencies' : 'Join your agency!'}
                subtitle={hasDepartments ? 'Do you belong to or have an assignment with another agency? Join additional agencies here...' : 'If you feel like you have a good grasp on the features of Evertel, go ahead and join your agency below...'}
            />
            <ModalBody>
                <Row>
                    <Col>
                        {/* DEPT ACCESS CODE BLOCK */}
                        <InfoBox
                            color="info"
                            className="text-center mt-4 pt-4"
                        >
                            <label>
                                <h3 className="fw-bold">
                                    Enter the Agency Access Code
                                </h3>
                                <p>
                                    You must have a valid Agency Access Code for
                                    the agency you are trying to join
                                </p>
                                <FormInput
                                    type="text"
                                    name="token"
                                    value={token}
                                    placeholder="Enter your Agency Access Code"
                                    leftIcon="key"
                                    leftIconColor="body-color"
                                    isValid={tokenValid}
                                    onChange={onTokenChange}
                                    className="pt-2 pb-2"
                                />
                            </label>

                            {/* ERROR BLOCK */}
                            <div aria-live="polite">
                                {error && <InfoBox color="danger">{error}</InfoBox>}
                            </div>

                            <div className="text-center mb-4">
                                {/* JOIN BTTN */}
                                {!tokenValid && (
                                    <Button
                                        color="primary"
                                        className="my-3"
                                        loading={isLoading}
                                        disabled={isLoading}
                                        type="submit"
                                        onClick={consumeInviteToken}
                                    >
                                        Join Agency
                                    </Button>
                                )}

                                {/* YOU HAVE BEEN VERIFIED */}
                                {tokenValid && (
                                    <SuccessCheckmark message="You have been verified and added to the agency!" />
                                )}
                            </div>
                        </InfoBox>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter className="text-center">
                <Button color="link" size="sm" onClick={onCloseWindow}>
                    {tokenValid ? 'Close' : 'Cancel'}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export { JoinDepartmentModal }
